import { useEffect, useState, type FormEvent, type JSX } from 'react'
import { Smartphone, X, Copy, Check, Loader2 } from 'lucide-react'
import { toDataURL } from 'qrcode'
import type { MfaSetup } from '../../api/mfa.js'

const R = 'rounded-[3px]'
const BTN_PRIMARY = `flex h-9 items-center gap-1.5 ${R} bg-[#3366FF] px-4 text-[13px] font-semibold text-white transition-colors hover:bg-[#2952CC] disabled:cursor-not-allowed disabled:opacity-40`
const BTN_SECONDARY = `flex h-9 items-center gap-1.5 ${R} border border-slate-200 bg-white px-4 text-[13px] font-semibold text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40`
const INPUT = `w-full ${R} border border-slate-200 bg-white px-3 py-2 text-[13px] text-slate-700 outline-none transition-colors focus:border-[#3366FF] focus:ring-2 focus:ring-[#3366FF]/10`

interface MfaSetupDialogProps {
  setup: MfaSetup
  onVerify: (code: string) => void
  onCancel: () => void
  loading: boolean
  error: string | null
}

export function MfaSetupDialog(props: MfaSetupDialogProps): JSX.Element {
  const { setup, onVerify, onCancel, loading, error } = props
  const [totpCode, setTotpCode] = useState('')
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    setQrDataUrl(null)
    toDataURL(setup.otpauthUrl, { width: 200, margin: 2 })
      .then((url: string) => setQrDataUrl(url))
      .catch(() => {})
  }, [setup.otpauthUrl])

  async function copySecret(): Promise<void> {
    try {
      await navigator.clipboard.writeText(setup.secret)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // ignore
    }
  }

  function handleSubmit(e: FormEvent): void {
    e.preventDefault()
    if (totpCode.length === 6) {
      onVerify(totpCode)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Set up MFA"
    >
      <div className={`w-full max-w-md border border-slate-200 bg-white p-6 shadow-xl ${R}`}>
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <span
              className={`flex h-10 w-10 shrink-0 items-center justify-center ${R} bg-blue-50 text-[#3366FF]`}
            >
              <Smartphone className="h-5 w-5" />
            </span>
            <div>
              <h3 className="text-[17px] font-bold leading-tight tracking-tight text-[#0f172a]">
                Set up MFA
              </h3>
              <p className="mt-0.5 text-[12.5px] text-slate-500">
                Scan the QR code to link your authenticator app.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className={`${R} p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700`}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

        <div className="flex justify-center">
          {qrDataUrl ? (
            <img
              src={qrDataUrl}
              alt="MFA QR code"
              className={`h-48 w-48 border border-slate-200 p-2 ${R}`}
            />
          ) : (
            <div
              className={`flex h-48 w-48 items-center justify-center border border-slate-200 bg-slate-50 ${R}`}
            >
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          )}
        </div>

        <div className="mt-5">
          <p className="text-[12.5px] text-slate-500">
            Cannot scan? Enter this secret manually:
          </p>
          <div className="mt-2 flex gap-2">
            <input
              readOnly
              value={setup.secret}
              className={`${INPUT} flex-1 font-mono`}
            />
            <button
              type="button"
              onClick={copySecret}
              className={BTN_SECONDARY}
              aria-label="Copy secret"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-5 space-y-3">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            placeholder="6-digit code"
            value={totpCode}
            onChange={(e) =>
              setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))
            }
            className={INPUT}
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              className={BTN_PRIMARY}
              disabled={totpCode.length !== 6 || loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              Verify & enable
            </button>
            <button type="button" className={BTN_SECONDARY} onClick={onCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
